import { secondaryTextClassName } from '@monecolier/ui';

/**
 * UX-44 — "Répartition des élèves" (Accueil). **Vérifié AVANT tout code,
 * comme demandé par l'Étape 0 du ticket** : aucun composant de type
 * donut/graphique n'existe dans `packages/ui` (grep exhaustif) — construit
 * ICI, en local (`apps/web-admin/src/components`), pas dans `packages/ui` :
 * UN SEUL consommateur réel à ce jour (cette page), même précédent que
 * `TodayCollectionsPanel`/`MissingInvoicesBanner` (extraits vers
 * `packages/ui` seulement le jour où un SECOND consommateur réel apparaît,
 * jamais par anticipation).
 *
 * SVG fait main (deux arcs `<circle>` superposés, `strokeDasharray`/
 * `strokeDashoffset`) — même discipline "zéro dépendance de charting" déjà
 * établie par ce projet (`packages/ui/src/Sidebar.tsx`, icône hamburger en
 * SVG fait main).
 */
const SIZE = 120;
const STROKE_WIDTH = 16;
const RADIUS = (SIZE - STROKE_WIDTH) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;
const CENTER = SIZE / 2;

interface SexBreakdownDonutProps {
  male: number;
  female: number;
}

export function SexBreakdownDonut({ male, female }: SexBreakdownDonutProps) {
  const total = male + female;

  if (total === 0) {
    return (
      <p className={secondaryTextClassName} data-testid="sex-breakdown-empty">
        Aucun élève inscrit pour l&apos;année active.
      </p>
    );
  }

  const femaleFraction = female / total;
  const femaleLength = femaleFraction * CIRCUMFERENCE;
  const maleLength = CIRCUMFERENCE - femaleLength;
  const femalePercent = Math.round(femaleFraction * 100);
  const malePercent = 100 - femalePercent;

  return (
    <div className="flex flex-wrap items-center gap-6" data-testid="sex-breakdown-donut">
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={`${femalePercent}% de filles, ${malePercent}% de garçons`}
      >
        <circle cx={CENTER} cy={CENTER} r={RADIUS} fill="none" strokeWidth={STROKE_WIDTH} className="stroke-gray-200" />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE_WIDTH}
          className="stroke-rose-500"
          strokeDasharray={`${femaleLength} ${CIRCUMFERENCE}`}
          strokeDashoffset={0}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
          data-testid="sex-breakdown-donut-female-arc"
        />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          fill="none"
          strokeWidth={STROKE_WIDTH}
          className="stroke-blue-500"
          strokeDasharray={`${maleLength} ${CIRCUMFERENCE}`}
          strokeDashoffset={-femaleLength}
          transform={`rotate(-90 ${CENTER} ${CENTER})`}
          data-testid="sex-breakdown-donut-male-arc"
        />
      </svg>
      <ul className="flex flex-col gap-2 text-sm">
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="h-3 w-3 shrink-0 rounded-full bg-rose-500" />
          <span className="font-semibold text-gray-900">{female}</span>
          <span className={secondaryTextClassName}>
            fille{female > 1 ? 's' : ''} ({femalePercent}%)
          </span>
        </li>
        <li className="flex items-center gap-2">
          <span aria-hidden="true" className="h-3 w-3 shrink-0 rounded-full bg-blue-500" />
          <span className="font-semibold text-gray-900">{male}</span>
          <span className={secondaryTextClassName}>
            garçon{male > 1 ? 's' : ''} ({malePercent}%)
          </span>
        </li>
      </ul>
    </div>
  );
}
